import EventEmitter from 'eventemitter3';
import Environment from './Environment';

export enum TickerEvent {
    TICK = 'tick',
}
export default class Ticker extends EventEmitter {
    private started: boolean;
    private frame: any;
    private _fpsLimit: number;
    private frameInterval: number;
    private now: number;
    private previousTime: number;
    private elapsedTime: number;
    private delta: number;
    private stats: Stats;

    private remainder: number = 0;

    public start(): void {
        if (this.started) return;

        this.now = window.performance.now();
        this.previousTime = this.now;
        this.elapsedTime = 0;
        this.started = true;

        if (process && process.env.NODE_ENV === Environment.DEVELOPMENT) {
            const Stats = require('stats.js');
            this.stats = new Stats();
            this.stats.showPanel(0);
            this.stats.dom.style.cssText =
                'position:absolute;bottom:0;right:0;cursor:pointer;opacity:0.8;z-index:10000';
            document.body.appendChild(this.stats.dom);

            this.debugTick = this.debugTick.bind(this);
            this.frame = requestAnimationFrame(this.debugTick);
        } else {
            this.tick = this.tick.bind(this);
            this.frame = requestAnimationFrame(this.tick);
        }
    }

    public stop(): void {
        if (!this.started) return;

        this.started = false;
        cancelAnimationFrame(this.frame);
    }

    public debugTick(time: number): void {
        this.frame = requestAnimationFrame(this.debugTick);

        this.stats.begin();
        this.now = time;

        this.delta = this.now - this.previousTime;
        this.elapsedTime += this.delta;
        if (this.delta > this.frameInterval) {
            this.remainder = this.delta % this.frameInterval;
            this.previousTime = this.now - this.remainder;
            this.delta -= this.remainder;
            this.emit(TickerEvent.TICK);
            this.stats.end();
        }
    }

    public tick(time: number): void {
        this.frame = requestAnimationFrame(this.tick);

        this.now = time;

        this.delta = this.now - this.previousTime;
        this.elapsedTime += this.delta;
        if (this.delta > this.frameInterval) {
            this.remainder = this.delta % this.frameInterval;
            this.previousTime = this.now - this.remainder;
            this.delta -= this.remainder;
            this.emit(TickerEvent.TICK);
        }
    }

    public get fpsLimit(): number {
        return this._fpsLimit;
    }

    public set fpsLimit(value: number) {
        this._fpsLimit = value;
        this.frameInterval = 1000 / value;
    }

    public setImmediate(resolve: (value?: PromiseLike<any> | any) => void) {
        const onTick = () => {
            this.off(TickerEvent.TICK, onTick);
            resolve();
        };

        this.on(TickerEvent.TICK, onTick);
    }

    public setInterval(
        resolve: (value?: PromiseLike<any> | any) => void,
        interval: number
    ): (interval: number) => void {
        let t = 0;
        const onTick = () => {
            t += this.getDeltaTime();
            if (t >= interval) {
                t = 0;
                resolve();
            }
        };

        this.on(TickerEvent.TICK, onTick);

        return onTick;
    }

    public clearInterval(interval: (interval: number) => void) {
        if (!interval) return;
        this.off(TickerEvent.TICK, interval);
    }

    public setTimeout(
        resolve: (value?: PromiseLike<any> | any) => void,
        time: number
    ): (interval: number) => void {
        if (time <= 0) resolve();
        let t = 0;
        const onTick = () => {
            t += this.getDeltaTime();
            if (t >= time) {
                this.off(TickerEvent.TICK, onTick);
                resolve();
            }
        };

        this.on(TickerEvent.TICK, onTick);

        return onTick;
    }

    public clearTimeout(interval: (interval: number) => void) {
        if (!interval) return;
        this.off(TickerEvent.TICK, interval);
    }

    public setTimeoutPromise(time: number) {
        return new Promise(resolve => {
            this.setTimeout(resolve, time);
        });
    }

    public getElapsedTime(): number {
        return this.elapsedTime;
    }

    public getDeltaTime(): number {
        return this.delta;
    }
}
